// Riportok · pivot (5.3): what a tag value was worth, country by country. RPR
// is revenue per recipient and RPE revenue per engaged (per unique click),
// both in euro-cent thousandths, so they stay whole numbers all the way from
// the query to the screen. A campaign with two values in one dimension counts
// in both rows — the question is "what did the campaigns with this value
// bring", and the footnote says so.

import { useEffect, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { fetchPivot, fetchTagDimensions } from "@/lib/api";
import type { PivotReport } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { DataTable } from "@/components/emc/DataTable";
import { Field } from "@/components/emc/Field";
import { PageHeader } from "@/components/emc/PageHeader";
import { SourceMark } from "@/components/emc/SourceMark";
import { Toolbar } from "@/components/emc/Toolbar";
import { Callout, EmptyState, ErrorState } from "@/components/emc/states";
import { count } from "@/components/emc/format";
import { euro, fxNote, permilleText, warehouseNote } from "@/pages/reports/model";
import { Flag } from "@/components/emc/Flag";
import { KNOWN_COUNTRIES } from "@/pages/contacts/model";

// Every country there is an account for, HR, NL and SI included.
const COUNTRIES = KNOWN_COUNTRIES;

/** Euro-cent thousandths back into euro cents: "0,42 ct". */
function perThousand(value: number | null): string {
  if (value === null) return "—";
  return `${(value / 1000).toLocaleString("hu-HU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ct`;
}

type PivotRow = PivotReport["rows"][number];

// A value's total row has no country; it is set in the strong weight.
const COLUMNS: ColumnDef<PivotRow>[] = [
  { id: "value", header: "Érték", cell: ({ row }) => row.original.value },
  {
    id: "country",
    header: "Ország",
    cell: ({ row }) => row.original.country ?? "összesen",
    meta: { mono: true },
  },
  { id: "campaigns", header: "Kampány", cell: ({ row }) => count(row.original.campaigns), meta: { align: "right" } },
  { id: "sent", header: "Küldve", cell: ({ row }) => count(row.original.sent), meta: { align: "right" } },
  {
    id: "delivered",
    header: "Kézbesítve",
    cell: ({ row }) => count(row.original.delivered),
    meta: { align: "right" },
  },
  {
    id: "clicks",
    header: "Egyedi kattintás",
    cell: ({ row }) => count(row.original.unique_clicks),
    meta: { align: "right" },
  },
  { id: "ctr", header: "CTR", cell: ({ row }) => permilleText(row.original.ctr_permille), meta: { align: "right" } },
  { id: "orders", header: "Rendelés", cell: ({ row }) => count(row.original.orders), meta: { align: "right" } },
  {
    id: "revenue",
    header: "Bevétel",
    cell: ({ row }) => (
      <span title={fxNote(row.original.fx_first_day, row.original.fx_last_day) || undefined}>
        {euro(row.original.revenue_eur_cents)}
      </span>
    ),
    meta: { align: "right" },
  },
  { id: "rpr", header: "RPR", cell: ({ row }) => perThousand(row.original.rpr), meta: { align: "right" } },
  { id: "rpe", header: "RPE", cell: ({ row }) => perThousand(row.original.rpe), meta: { align: "right" } },
];

export default function Pivot() {
  const [dimensions] = useLoad(fetchTagDimensions, []);
  const [dimension, setDimension] = useState("");
  const [countries, setCountries] = useState<string[]>([]);
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [asked, setAsked] = useState(0);

  // The first dimension, asked at once: an empty pivot page asks the reader
  // to do what the page could have done.
  useEffect(() => {
    const first = dimensions.data?.[0];
    if (first !== undefined && dimension === "") {
      setDimension(first.key);
      setAsked((value) => value + 1);
    }
  }, [dimensions.data, dimension]);

  const [report, reload] = useLoad<PivotReport | null>(
    () =>
      dimension === ""
        ? Promise.resolve(null)
        : fetchPivot({
            dimension,
            countries: countries.join(","),
            since: since || undefined,
            until: until || undefined,
          }),
    [asked],
  );

  function toggle(code: string) {
    setCountries(countries.includes(code) ? countries.filter((item) => item !== code) : [...countries, code]);
  }

  // The pivot's cells say "—" where the warehouse did not answer: the note
  // names the same mark the cells carry.
  const note = report.data ? warehouseNote(report.data, "—") : null;

  return (
    <>
      <PageHeader
        title="Riportok · pivot"
        description={
          <>
            Egy tag-dimenzió értékei országonként: mennyit hozott az, amiben ez az érték volt. A
            bevétel a Klaviyo-ablaké, hogy a Klaviyo-számmal összemérhető legyen.
          </>
        }
      />

      <div className="flex flex-col gap-6">
        {dimensions.error !== null && <ErrorState message={dimensions.error} />}

        <Card>
          <CardHeader>
            <CardTitle>Lekérdezés</CardTitle>
            <CardDescription>
              A napok a kampány indulása szerint számítanak (ha nem indult, a létrehozása szerint); a
              záró nap is benne van.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Toolbar>
              <Field label="Dimenzió" htmlFor="pivot-dimension">
                <NativeSelect id="pivot-dimension" value={dimension} onChange={(event) => setDimension(event.target.value)}>
                  {(dimensions.data ?? []).map((row) => (
                    <option key={row.key} value={row.key}>
                      {row.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Kezdet" htmlFor="pivot-since">
                <Input
                  id="pivot-since"
                  className="w-auto"
                  type="date"
                  value={since}
                  onChange={(event) => setSince(event.target.value)}
                />
              </Field>
              <Field label="Vég" htmlFor="pivot-until">
                <Input
                  id="pivot-until"
                  className="w-auto"
                  type="date"
                  value={until}
                  onChange={(event) => setUntil(event.target.value)}
                />
              </Field>
              <Button type="button" disabled={dimension === ""} onClick={() => setAsked((value) => value + 1)}>
                Lekérdezés
              </Button>
            </Toolbar>
            <fieldset className="m-0 flex flex-wrap items-center gap-x-4 gap-y-2 border-0 p-0">
              <legend className="caps-label mb-2">Országok (egy sem: mind)</legend>
              {COUNTRIES.map((code) => (
                <label key={code} className="flex cursor-pointer items-center gap-1.5 font-medium">
                  <Checkbox checked={countries.includes(code)} onChange={() => toggle(code)} />
                  <Flag code={code} />
                  {code}
                </label>
              ))}
            </fieldset>
          </CardContent>
        </Card>

        {note !== null && (
          <Callout tone="warn">
            {note.text} Ez a kattintást, a CTR-t és az RPE-t érinti.
            {note.reason && (
              <span className="mt-1 block truncate text-label text-muted-foreground" title={note.reason}>
                {note.reason}
              </span>
            )}
          </Callout>
        )}

        <Card>
          <CardHeader>
            <CardTitle>{report.data?.dimension_label ?? "Értékek"}</CardTitle>
            <CardDescription>
              Értékenként az országok, alattuk az összesen-sor.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {report.error !== null && <ErrorState message={report.error} onRetry={reload} />}
            <p className="m-0 flex flex-wrap items-center gap-x-3 text-label text-muted-foreground">
              <span>
                <SourceMark source="Postgres" />: kampány, küldve, kézbesítve, rendelés, bevétel, RPR
              </span>
              <span>
                <SourceMark source="ClickHouse" />: egyedi kattintás, CTR, RPE
              </span>
            </p>
            <DataTable
              label="Pivot"
              variant="plain"
              className="-mx-5.5 border-t border-separator"
              columns={COLUMNS}
              data={report.data?.rows ?? []}
              loading={report.data === null && report.error === null && dimension !== ""}
              getRowId={(row) => `${row.value}-${row.country ?? "osszes"}`}
              rowClassName={(row) => (row.country === null ? "bg-surface-2 font-semibold" : undefined)}
              empty={<EmptyState bare title="Ebben a tartományban nincs ilyen taggel ellátott kampány." />}
            />
            {report.data && <p className="m-0 text-label text-muted-foreground">{report.data.note}</p>}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
